import { Box, Link, Typography } from '@mui/material';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import React, { useEffect, useState } from 'react';

import type { K8sObject } from './types';

interface CatalogMatch {
  kind: string;
  namespace: string;
  name: string;
  owner?: string;
}

export function CatalogOwnership({ object }: { object: K8sObject }) {
  const catalogApi = useApi(catalogApiRef);
  const [matches, setMatches] = useState<CatalogMatch[]>([]);
  const kubernetesId =
    object.metadata.labels?.['backstage.io/kubernetes-id'] ??
    object.metadata.annotations?.['backstage.io/kubernetes-id'];

  useEffect(() => {
    let cancelled = false;
    if (!kubernetesId) {
      setMatches([]);
      return undefined;
    }
    catalogApi
      .getEntities({
        filter: {
          'metadata.annotations.backstage.io/kubernetes-id': kubernetesId,
        },
        fields: ['kind', 'metadata.namespace', 'metadata.name', 'spec.owner'],
      })
      .then(result => {
        if (cancelled) return;
        setMatches(
          result.items.map(entity => ({
            kind: entity.kind,
            namespace: entity.metadata.namespace ?? 'default',
            name: entity.metadata.name,
            owner:
              typeof entity.spec?.owner === 'string'
                ? entity.spec.owner
                : undefined,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setMatches([]);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, kubernetesId]);

  if (!kubernetesId) return null;
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.25,
        border: '1px solid #4f5255',
        borderRadius: 1,
        bgcolor: '#272727',
      }}
    >
      <Typography sx={{ color: '#c7c7c7', fontSize: 11, fontWeight: 700 }}>
        BACKSTAGE OWNERSHIP
      </Typography>
      <Typography sx={{ color: '#f0f0f0', fontSize: 12, mt: 0.5 }}>
        Kubernetes ID: {kubernetesId}
      </Typography>
      {matches.length ? (
        matches.map(match => (
          <Typography key={`${match.kind}/${match.name}`} sx={{ fontSize: 12 }}>
            <Link
              href={`/catalog/${match.namespace}/${match.kind.toLowerCase()}/${
                match.name
              }`}
              sx={{ color: '#8bc1f7' }}
            >
              {match.kind}/{match.name}
            </Link>
            {match.owner ? ` · owned by ${match.owner}` : ''}
          </Typography>
        ))
      ) : (
        <Typography sx={{ color: '#c7c7c7', fontSize: 11.5, mt: 0.5 }}>
          No matching catalog entity was found.
        </Typography>
      )}
    </Box>
  );
}
